import React, { useState, useEffect } from 'react';
import { HiPlus, HiPencil, HiTrash, HiSearch, HiGlobe, HiLockClosed, HiCubeTransparent, HiChatAlt2, HiClipboardCopy } from 'react-icons/hi';
import toast from 'react-hot-toast';
import api from '../utils/api';
import VoiceDropdown from './common/VoiceDropdown';
import ChatWidget from './common/ChatWidget';
import { AppPageShell, AppLoadingState, Button, EmptyState, PageHeader } from './common/ui';

const WebAgents = () => {
  // State management
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [dialogFlows, setDialogFlows] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    prompt: '',
    voice: '',
    webhook: '',
    analysis_schema: {},
    metadata: {},
    pathway_id: '',
    language: 'ENG',
    model: 'enhanced',
    first_sentence: '',
    tools: [],
    dynamic_data: {},
    interruption_threshold: 100,
    keywords: [],
    max_duration: 30
  });

  // Fetch initial data on component mount
  useEffect(() => {
    fetchAgents();
    fetchDialogFlows();
  }, []);

  const fetchDialogFlows = async () => {
    try {
      const flowsResponse = await api.getdialogFlows();
      let pathways = [];
      if (Array.isArray(flowsResponse)) {
        pathways = flowsResponse;
      } else if (flowsResponse.pathways) {
        pathways = flowsResponse.pathways;
      } else if (flowsResponse.data && flowsResponse.data.pathways) {
        pathways = flowsResponse.data.pathways;
      }
      
      const mappedFlows = pathways.map(pathway => ({
        id: pathway.id,
        name: pathway.name || `Pathway ${pathway.id}`
      }));
      setDialogFlows(mappedFlows);
    } catch (error) {
      console.error('Error fetching dialog flows:', error);
      toast.error('Failed to fetch dialog flows');
    }
  };

  const fetchAgents = async () => {
    try {
      setLoading(true);
      const response = await api.getWebAgents();
      setAgents(response.agents || []);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAgent = async () => {
    try {
      setLoading(true);
      
      // Validate required fields
      if (!formData.name.trim()) {
        throw new Error('Agent name is required');
      }
      
      if (!formData.prompt && !formData.pathway_id) {
        throw new Error('Either Prompt or Pathway ID is required');
      }

      // Prepare the data
      const data = {
        ...formData,
        name: formData.name.trim(),
        // Ensure JSON fields are objects/arrays
        analysis_schema: typeof formData.analysis_schema === 'string' ? 
          JSON.parse(formData.analysis_schema) : formData.analysis_schema,
        metadata: typeof formData.metadata === 'string' ? 
          JSON.parse(formData.metadata) : formData.metadata,
        tools: typeof formData.tools === 'string' ? 
          JSON.parse(formData.tools) : formData.tools,
        dynamic_data: typeof formData.dynamic_data === 'string' ? 
          JSON.parse(formData.dynamic_data) : formData.dynamic_data,
        // Convert empty strings to null for optional fields
        pathway_id: formData.pathway_id || null,
        first_sentence: formData.first_sentence || null,
        webhook: formData.webhook || null,
        voice: formData.voice || null
      };

      await api.createWebAgent(data);
      toast.success(`Web agent "${formData.name}" created successfully`);
      fetchAgents();
      handleCloseCreateDialog();
    } catch (err) {
      console.error('Error creating web agent:', err);
      toast.error(err.message || 'Failed to create web agent');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAgent = async () => {
    try {
      if (!selectedAgent) return;
      
      setLoading(true);
      
      // Validate required fields
      if (!formData.name.trim()) {
        throw new Error('Agent name is required');
      }
      
      if (!formData.prompt && !formData.pathway_id) {
        throw new Error('Either Prompt or Pathway ID is required');
      }

      // Prepare the data
      const data = {
        ...formData,
        name: formData.name.trim(),
        // Ensure JSON fields are objects/arrays
        analysis_schema: typeof formData.analysis_schema === 'string' ? 
          JSON.parse(formData.analysis_schema) : formData.analysis_schema,
        metadata: typeof formData.metadata === 'string' ? 
          JSON.parse(formData.metadata) : formData.metadata,
        tools: typeof formData.tools === 'string' ? 
          JSON.parse(formData.tools) : formData.tools,
        dynamic_data: typeof formData.dynamic_data === 'string' ? 
          JSON.parse(formData.dynamic_data) : formData.dynamic_data,
        // Convert empty strings to null for optional fields
        pathway_id: formData.pathway_id || null,
        first_sentence: formData.first_sentence || null,
        webhook: formData.webhook || null,
        voice: formData.voice || null
      };

      await api.updateWebAgent(selectedAgent.agent_id, data);
      toast.success(`Web agent "${formData.name}" updated successfully`);
      fetchAgents();
      handleCloseEditDialog();
    } catch (err) {
      console.error('Error updating web agent:', err);
      toast.error(err.message || 'Failed to update web agent');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAgent = async (agent) => {
    if (!window.confirm(`Are you sure you want to delete the agent "${agent.name || 'Unnamed Agent'}"?`)) {
      return;
    }

    try {
      setLoading(true);
      await api.deleteWebAgent(agent.agent_id);
      await fetchAgents();
      toast.success(`Web agent "${agent.name || 'Unnamed Agent'}" deleted successfully`);
    } catch (error) {
      console.error('Error deleting web agent:', error);
      toast.error(error.message || 'Failed to delete web agent');
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePublic = async (agent) => {
    try {
      const newStatus = !agent.isPublic;
      await api.setAgentPublicStatus(agent.agent_id, newStatus, agent.name);
      
      // Update the local state
      setAgents(prevAgents => prevAgents.map(a => 
        a.agent_id === agent.agent_id ? { ...a, isPublic: newStatus } : a
      ));
      
      toast.success(`Agent "${agent.name || 'Unnamed Agent'}" is now ${newStatus ? 'public' : 'private'}`);
    } catch (error) {
      console.error('Error toggling public status:', error);
      toast.error('Failed to update agent public status');
    }
  };

  // Dialog handlers
  const handleOpenCreateDialog = () => {
    setOpenCreateDialog(true);
    setFormData({
      name: '',
      prompt: '',
      voice: '',
      webhook: '',
      analysis_schema: {},
      metadata: {},
      pathway_id: '',
      language: 'ENG',
      model: 'enhanced',
      first_sentence: '',
      tools: [],
      dynamic_data: {},
      interruption_threshold: 100,
      keywords: [],
      max_duration: 30
    });
  };

  const handleCloseCreateDialog = () => {
    setOpenCreateDialog(false);
    setFormData({
      name: '',
      prompt: '',
      voice: '',
      webhook: '',
      analysis_schema: {},
      metadata: {},
      pathway_id: '',
      language: 'ENG',
      model: 'enhanced',
      first_sentence: '',
      tools: [],
      dynamic_data: {},
      interruption_threshold: 100,
      keywords: [],
      max_duration: 30
    });
  };

  const handleOpenEditDialog = (agent) => {
    setSelectedAgent(agent);
    setFormData({
      name: agent.name || '',
      prompt: agent.prompt || '',
      voice: agent.voice || '',
      webhook: agent.webhook || '',
      analysis_schema: agent.analysis_schema || {},
      metadata: agent.metadata || {},
      pathway_id: agent.pathway_id || '',
      language: agent.language || 'ENG',
      model: agent.model || 'enhanced',
      first_sentence: agent.first_sentence || '',
      tools: agent.tools || [],
      dynamic_data: agent.dynamic_data || {},
      interruption_threshold: agent.interruption_threshold || 100,
      keywords: agent.keywords || [],
      max_duration: agent.max_duration || 30
    });
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setSelectedAgent(null);
    setFormData({
      name: '',
      prompt: '',
      voice: '',
      webhook: '',
      analysis_schema: {},
      metadata: {},
      pathway_id: '',
      language: 'ENG',
      model: 'enhanced',
      first_sentence: '',
      tools: [],
      dynamic_data: {},
      interruption_threshold: 100,
      keywords: [],
      max_duration: 30
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Handle mutual exclusivity between prompt and pathway_id
    if (name === 'pathway_id') {
      setFormData(prev => ({
        ...prev,
        [name]: value,
        prompt: value ? '' : prev.prompt // Clear prompt if pathway_id is set
      }));
    } else if (name === 'prompt') {
      setFormData(prev => ({
        ...prev,
        [name]: value,
        pathway_id: value ? '' : prev.pathway_id // Clear pathway_id if prompt is set
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleVoiceSelect = (voice) => {
    setSelectedVoice(voice);
    setFormData(prev => ({
      ...prev,
      voice: voice.id
    }));
  };

  // Filter agents based on search query
  const filteredAgents = agents.filter(agent => {
    const searchString = searchQuery.toLowerCase();
    return (
      (agent.name || '').toLowerCase().includes(searchString) ||
      (agent.prompt || '').toLowerCase().includes(searchString) ||
      (agent.voice || '').toLowerCase().includes(searchString) ||
      (agent.agent_id || '').toLowerCase().includes(searchString)
    );
  });

  if (loading && agents.length === 0) {
    return <AppLoadingState layout="section" showLabel={false} />;
  }

  return (
    <AppPageShell maxWidthClass="max-w-7xl" className="web-agents-page">
      <PageHeader
        eyebrow="Resources"
        title="Web Agents"
        description="Create and manage embeddable voice agents for websites and customer-facing flows."
        actions={(
          <Button
            onClick={handleOpenCreateDialog}
          >
            <HiPlus className="-ml-1 h-5 w-5" />
            Create New Agent
          </Button>
        )}
      />

      {/* Search Bar */}
      <div className="mb-5 sm:mb-6">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <HiSearch className="h-4 w-4 sm:h-5 sm:w-5 text-slate-400 dark:text-slate-500" />
          </div>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="app-field block w-full pl-9 sm:pl-10 pr-3 py-3 leading-5 placeholder-gray-500 dark:placeholder-slate-500 focus:outline-none text-sm"
            placeholder="Search by name, prompt, voice, or ID..."
          />
        </div>
      </div>

      {/* Agents Grid */}
      {filteredAgents.length === 0 ? (
        <EmptyState
          icon={<HiChatAlt2 className="h-5 w-5" />}
          title={searchQuery ? 'No web agents found' : 'No web agents yet'}
          description={searchQuery ? 'Adjust the search query to find another agent.' : 'Create a web agent to preview and embed a voice experience.'}
          actions={!searchQuery && (
            <Button onClick={handleOpenCreateDialog}>
              <HiPlus className="-ml-1 h-5 w-5" />
              Create New Agent
            </Button>
          )}
        />
      ) : (
      <div className="web-agents-grid">
        {filteredAgents.map((agent) => (
          <div
            key={agent.agent_id}
            className="web-agent-card"
          >
            <div className="web-agent-card-header">
              <div
                className={`rounded-full h-9 w-9 flex items-center justify-center flex-shrink-0 border ${
                  agent.pathway_id
                    ? 'bg-violet-100 text-violet-700 border-violet-200/80 dark:bg-violet-950/55 dark:text-violet-200 dark:border-violet-500/25'
                    : 'bg-teal-100 text-teal-800 border-teal-200/70 dark:bg-teal-950/45 dark:text-teal-200 dark:border-teal-500/25'
                }`}
              >
                {agent.pathway_id ? (
                  <HiCubeTransparent className="h-4 w-4" />
                ) : (
                  <HiChatAlt2 className="h-4 w-4" />
                )}
              </div>
              <h3 className="web-agent-card-title">
                {agent.name || 'Unnamed Agent'}
              </h3>
            </div>
            <div className="web-agent-card-body">
              <div className="flex justify-between items-start gap-3">
                <div className="min-w-0 flex-1 mr-2 sm:mr-4">
                  <p className="web-agent-id">
                    ID: <span className="font-mono ml-1 truncate">{agent.agent_id.substring(0, 8)}...</span>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(agent.agent_id);
                        toast.success('Agent ID copied to clipboard', { duration: 2000 });
                      }}
                      className="ml-1 p-0.5 text-slate-400 hover:text-teal-600 dark:text-slate-500 dark:hover:text-teal-400 transition-colors rounded flex-shrink-0"
                      title="Copy ID to clipboard"
                    >
                      <HiClipboardCopy className="h-3.5 w-3.5" />
                    </button>
                  </p>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button
                    onClick={() => handleTogglePublic(agent)}
                    className={`p-1 sm:p-1.5 rounded-md transition-colors flex items-center justify-center border ${
                      agent.isPublic
                        ? 'text-emerald-700 hover:text-emerald-800 bg-emerald-50 hover:bg-emerald-100 border-emerald-200/60 dark:text-emerald-300 dark:hover:text-emerald-200 dark:bg-emerald-950/40 dark:hover:bg-emerald-900/45 dark:border-emerald-500/25'
                        : 'text-slate-600 hover:text-slate-800 bg-slate-100 hover:bg-slate-200/90 border-slate-200/80 dark:text-slate-400 dark:hover:text-slate-200 dark:bg-slate-800/70 dark:hover:bg-slate-700/80 dark:border-slate-600/50'
                    }`}
                    title={agent.isPublic ? 'Make Private' : 'Make Public'}
                  >
                    {agent.isPublic ? (
                      <HiGlobe className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                    ) : (
                      <HiLockClosed className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                    )}
                  </button>
                  <button
                    onClick={() => handleOpenEditDialog(agent)}
                    className="p-1 sm:p-1.5 text-teal-700 hover:text-teal-800 bg-teal-50 hover:bg-teal-100 dark:text-teal-300 dark:hover:text-teal-200 dark:bg-teal-950/45 dark:hover:bg-teal-900/55 border border-teal-200/70 dark:border-teal-500/25 rounded-md flex items-center justify-center"
                    title="Edit Agent"
                  >
                    <HiPencil className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteAgent(agent)}
                    className="p-1 sm:p-1.5 text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 dark:text-red-300 dark:hover:text-red-200 dark:bg-red-950/40 dark:hover:bg-red-900/45 border border-red-200/70 dark:border-red-500/20 rounded-md flex items-center justify-center"
                    title="Delete Agent"
                  >
                    <HiTrash className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  </button>
                </div>
              </div>
              
              {/* Prompt or Dialog Flow information */}
              <div className="web-agent-flow">
                {agent.pathway_id ? (
                  <p className="truncate">
                    <span className="font-medium">Flow:</span> {dialogFlows.find(flow => flow.id === agent.pathway_id)?.name || agent.pathway_id}
                  </p>
                ) : agent.prompt ? (
                  <p className="truncate">
                    <span className="font-medium">Prompt:</span> {agent.prompt.substring(0, 40)}...
                  </p>
                ) : (
                  <p>No prompt or dialog flow set</p>
                )}
              </div>

              <div className="web-agent-meta-grid">
                <div className="flex flex-col">
                  <span className="text-xs text-slate-500 dark:text-slate-400 font-semibold">Voice:</span>
                  <span>{agent.voice || 'josh'}</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-xs text-slate-500 dark:text-slate-400 font-semibold">Language:</span>
                  <span>{agent.language || 'ENG'}</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-xs text-slate-500 dark:text-slate-400 font-semibold">Model:</span>
                  <span className="capitalize">{agent.model || 'enhanced'}</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-xs text-slate-500 dark:text-slate-400 font-semibold">Status:</span>
                  <span className={`${agent.isPublic ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-600 dark:text-slate-300'}`}>
                    {agent.isPublic ? 'Public' : 'Private'}
                  </span>
                </div>
              </div>
              <div className="web-agent-preview">
                <ChatWidget
                  agentId={agent.agent_id}
                  name={agent.name || 'AI Agent'}
                  avatar={agent.voice ? agent.voice.substring(0, 2).toUpperCase() : 'JO'}
                  status="Ready to Talk"
                  isAuthenticated={true}
                  onError={(error) => {
                    console.error('Call error:', error);
                    toast.error(error.message || 'An error occurred with the call');
                  }}
                  className="w-full max-w-full"
                  compact
                />
              </div>
            </div>
          </div>
        ))}
      </div>
      )}

      {/* Create/Edit Dialog */}
      {(openCreateDialog || openEditDialog) && (
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-slate-900/60 dark:bg-black/75 backdrop-blur-[2px]" />
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white dark:bg-slate-900 border border-slate-200/90 dark:border-slate-600/55 rounded-xl px-4 pt-5 pb-4 text-left overflow-hidden shadow-2xl dark:shadow-black/50 transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6">
              <div>
                <h3 className="text-lg leading-6 font-semibold text-slate-900 dark:text-slate-100 mb-4">
                  {openCreateDialog ? 'Create New Web Agent' : 'Edit Web Agent'}
                </h3>
                <div className="mt-4 space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-md shadow-sm bg-white dark:bg-slate-950/50 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-teal-500/30 focus:border-teal-600 dark:focus:border-teal-500"
                        placeholder="Enter the name of the agent"
                        required
                      />
                      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                        Give your agent a descriptive name that you'll remember.
                      </p>
                    </div>

                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Dialog Flow
                      </label>
                      <select
                        name="pathway_id"
                        value={formData.pathway_id}
                        onChange={handleInputChange}
                        disabled={!!formData.prompt}
                        className={`w-full p-2 border border-slate-300 dark:border-slate-600 rounded-md shadow-sm focus:ring-2 focus:ring-teal-500/30 focus:border-teal-600 dark:focus:border-teal-500 ${
                          formData.prompt
                            ? 'bg-slate-100 dark:bg-slate-800/80 cursor-not-allowed text-slate-500 dark:text-slate-400'
                            : 'bg-white dark:bg-slate-950/50 text-slate-900 dark:text-slate-100'
                        }`}
                      >
                        <option value="">Select a Dialog Flow</option>
                        {dialogFlows.map(flow => (
                          <option key={flow.id} value={flow.id}>
                            {flow.name}
                          </option>
                        ))}
                      </select>
                      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                        {dialogFlows.length === 0 ? 'Loading available dialog flows...' : 
                         formData.prompt ? 'Dialog Flow selection is disabled while Prompt is filled' :
                         'Select a Dialog Flow or enter a Prompt below, but not both'}
                      </p>
                    </div>

                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Prompt {!formData.pathway_id && "*"}
                      </label>
                      <textarea
                        name="prompt"
                        rows="4"
                        value={formData.prompt}
                        onChange={handleInputChange}
                        disabled={!!formData.pathway_id}
                        className={`w-full p-2 border border-slate-300 dark:border-slate-600 rounded-md shadow-sm focus:ring-2 focus:ring-teal-500/30 focus:border-teal-600 dark:focus:border-teal-500 ${
                          formData.pathway_id
                            ? 'bg-slate-100 dark:bg-slate-800/80 cursor-not-allowed text-slate-500 dark:text-slate-400'
                            : 'bg-white dark:bg-slate-950/50 text-slate-900 dark:text-slate-100'
                        }`}
                        placeholder={formData.pathway_id ? 
                          "Prompt is disabled when Dialog Flow is selected" : 
                          "Provide instructions, relevant information, and examples of the ideal conversation flow"}
                        required={!formData.pathway_id}
                      />
                      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                        Aim for less than 2,000 characters. Simple, direct prompts are the most predictable and reliable.
                      </p>
                    </div>

                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Voice
                      </label>
                      <VoiceDropdown
                        onSelect={handleVoiceSelect}
                        selectedVoice={selectedVoice}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Language
                      </label>
                      <select
                        name="language"
                        value={formData.language}
                        onChange={handleInputChange}
                        className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-md shadow-sm bg-white dark:bg-slate-950/50 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-teal-500/30 focus:border-teal-600 dark:focus:border-teal-500"
                      >
                        <option value="auto">Auto Detect</option>
                        <option value="en">English</option>
                        <option value="en-US">English (US)</option>
                        <option value="en-GB">English (UK)</option>
                        <option value="en-AU">English (Australia)</option>
                        <option value="en-NZ">English (New Zealand)</option>
                        <option value="en-IN">English (India)</option>
                        <option value="zh">Chinese (Mandarin, Simplified)</option>
                        <option value="zh-CN">Chinese (Mandarin, Simplified, China)</option>
                        <option value="zh-Hans">Chinese (Mandarin, Simplified, Hans)</option>
                        <option value="zh-TW">Chinese (Mandarin, Traditional)</option>
                        <option value="zh-Hant">Chinese (Mandarin, Traditional, Hant)</option>
                        <option value="es">Spanish</option>
                        <option value="es-419">Spanish (Latin America)</option>
                        <option value="fr">French</option>
                        <option value="fr-CA">French (Canada)</option>
                        <option value="de">German</option>
                        <option value="el">Greek</option>
                        <option value="hi">Hindi</option>
                        <option value="hi-Latn">Hindi (Latin script)</option>
                        <option value="ja">Japanese</option>
                        <option value="ko">Korean</option>
                        <option value="ko-KR">Korean (Korea)</option>
                        <option value="pt">Portuguese</option>
                        <option value="pt-BR">Portuguese (Brazil)</option>
                        <option value="pt-PT">Portuguese (Portugal)</option>
                        <option value="it">Italian</option>
                        <option value="nl">Dutch</option>
                        <option value="pl">Polish</option>
                        <option value="ru">Russian</option>
                        <option value="sv">Swedish</option>
                        <option value="sv-SE">Swedish (Sweden)</option>
                        <option value="da">Danish</option>
                        <option value="da-DK">Danish (Denmark)</option>
                        <option value="fi">Finnish</option>
                        <option value="id">Indonesian</option>
                        <option value="ms">Malay</option>
                        <option value="tr">Turkish</option>
                        <option value="uk">Ukrainian</option>
                        <option value="bg">Bulgarian</option>
                        <option value="cs">Czech</option>
                        <option value="ro">Romanian</option>
                        <option value="sk">Slovak</option>
                        <option value="hu">Hungarian</option>
                        <option value="no">Norwegian</option>
                        <option value="vi">Vietnamese</option>
                        <option value="babel">Babel (Experimental)</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Model
                      </label>
                      <select
                        name="model"
                        value={formData.model}
                        onChange={handleInputChange}
                        className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-md shadow-sm bg-white dark:bg-slate-950/50 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-teal-500/30 focus:border-teal-600 dark:focus:border-teal-500"
                      >
                        <option value="base">Base</option>
                        <option value="enhanced">Enhanced (Recommended)</option>
                        <option value="turbo">Turbo</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Max Duration (minutes)
                      </label>
                      <input
                        type="number"
                        name="max_duration"
                        value={formData.max_duration}
                        onChange={handleInputChange}
                        className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-md shadow-sm bg-white dark:bg-slate-950/50 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-teal-500/30 focus:border-teal-600 dark:focus:border-teal-500"
                        min="1"
                        max="60"
                        placeholder="Default: 30"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Interruption Threshold
                      </label>
                      <input
                        type="number"
                        name="interruption_threshold"
                        value={formData.interruption_threshold}
                        onChange={handleInputChange}
                        className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-md shadow-sm bg-white dark:bg-slate-950/50 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-teal-500/30 focus:border-teal-600 dark:focus:border-teal-500"
                        min="50"
                        max="200"
                        placeholder="Default: 100"
                      />
                      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                        50: Quick responses, 100: Natural pace, 200: Very patient
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        First Sentence
                      </label>
                      <input
                        type="text"
                        name="first_sentence"
                        value={formData.first_sentence}
                        onChange={handleInputChange}
                        className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-md shadow-sm bg-white dark:bg-slate-950/50 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-teal-500/30 focus:border-teal-600 dark:focus:border-teal-500"
                        maxLength="200"
                        placeholder="Optional starting phrase"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Webhook URL
                      </label>
                      <input
                        type="url"
                        name="webhook"
                        value={formData.webhook}
                        onChange={handleInputChange}
                        className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-md shadow-sm bg-white dark:bg-slate-950/50 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-teal-500/30 focus:border-teal-600 dark:focus:border-teal-500"
                        placeholder="https://your-webhook-url.com"
                      />
                    </div>

                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Keywords
                      </label>
                      <input
                        type="text"
                        name="keywords"
                        value={Array.isArray(formData.keywords) ? formData.keywords.join(', ') : ''}
                        onChange={(e) => {
                          const keywords = e.target.value.split(',').map(k => k.trim()).filter(k => k);
                          setFormData(prev => ({ ...prev, keywords }));
                        }}
                        className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-md shadow-sm bg-white dark:bg-slate-950/50 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-teal-500/30 focus:border-teal-600 dark:focus:border-teal-500"
                        placeholder="Enter keywords separated by commas"
                      />
                      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                        Add boost factor with colon (e.g., "word:3"). Default boost is 2.
                      </p>
                    </div>

                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Analysis Schema (JSON)
                      </label>
                      <textarea
                        name="analysis_schema"
                        rows="3"
                        value={typeof formData.analysis_schema === 'object' ? 
                          JSON.stringify(formData.analysis_schema, null, 2) : formData.analysis_schema}
                        onChange={(e) => {
                          try {
                            const schema = JSON.parse(e.target.value);
                            setFormData(prev => ({ ...prev, analysis_schema: schema }));
                          } catch {
                            setFormData(prev => ({ ...prev, analysis_schema: e.target.value }));
                          }
                        }}
                        className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-md shadow-sm bg-white dark:bg-slate-950/50 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-teal-500/30 focus:border-teal-600 dark:focus:border-teal-500 font-mono text-sm"
                        placeholder='{"field": "type"}'
                      />
                    </div>

                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Metadata (JSON)
                      </label>
                      <textarea
                        name="metadata"
                        rows="3"
                        value={typeof formData.metadata === 'object' ? 
                          JSON.stringify(formData.metadata, null, 2) : formData.metadata}
                        onChange={(e) => {
                          try {
                            const metadata = JSON.parse(e.target.value);
                            setFormData(prev => ({ ...prev, metadata }));
                          } catch {
                            setFormData(prev => ({ ...prev, metadata: e.target.value }));
                          }
                        }}
                        className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-md shadow-sm bg-white dark:bg-slate-950/50 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-teal-500/30 focus:border-teal-600 dark:focus:border-teal-500 font-mono text-sm"
                        placeholder='{"key": "value"}'
                      />
                    </div>

                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Tools (JSON Array)
                      </label>
                      <textarea
                        name="tools"
                        rows="3"
                        value={Array.isArray(formData.tools) ? JSON.stringify(formData.tools, null, 2) : formData.tools}
                        onChange={(e) => {
                          try {
                            const tools = JSON.parse(e.target.value);
                            setFormData(prev => ({ ...prev, tools }));
                          } catch {
                            setFormData(prev => ({ ...prev, tools: e.target.value }));
                          }
                        }}
                        className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-md shadow-sm bg-white dark:bg-slate-950/50 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-teal-500/30 focus:border-teal-600 dark:focus:border-teal-500 font-mono text-sm"
                        placeholder="[]"
                      />
                    </div>

                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Dynamic Data (JSON)
                      </label>
                      <textarea
                        name="dynamic_data"
                        rows="3"
                        value={typeof formData.dynamic_data === 'object' ? 
                          JSON.stringify(formData.dynamic_data, null, 2) : formData.dynamic_data}
                        onChange={(e) => {
                          try {
                            const dynamicData = JSON.parse(e.target.value);
                            setFormData(prev => ({ ...prev, dynamic_data: dynamicData }));
                          } catch {
                            setFormData(prev => ({ ...prev, dynamic_data: e.target.value }));
                          }
                        }}
                        className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-md shadow-sm bg-white dark:bg-slate-950/50 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-teal-500/30 focus:border-teal-600 dark:focus:border-teal-500 font-mono text-sm"
                        placeholder="{}"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-6 sm:mt-8 sm:grid sm:grid-cols-2 sm:gap-3">
                <button
                  type="button"
                  onClick={openCreateDialog ? handleCreateAgent : handleUpdateAgent}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-teal-600 dark:bg-teal-600 text-base font-medium text-white hover:bg-teal-500 dark:hover:bg-teal-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500/50 dark:focus:ring-offset-slate-900 sm:text-sm"
                >
                  {openCreateDialog ? 'Create' : 'Update'}
                </button>
                <button
                  type="button"
                  onClick={openCreateDialog ? handleCloseCreateDialog : handleCloseEditDialog}
                  className="mt-3 sm:mt-0 w-full inline-flex justify-center rounded-md border border-slate-300 dark:border-slate-600 shadow-sm px-4 py-2 bg-white dark:bg-slate-800 text-base font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500/30 dark:focus:ring-offset-slate-900 sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AppPageShell>
  );
};

export default WebAgents; 
